import ActivityCard from "@components/ActivityCard";
import { Box } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { FlowStep } from "./useCombinationsFlow";
import { FilterOption } from "./useFilterConfig";
import { CombinationsSelections } from "./useSelectionMessage";

interface CombinationsFilterSectionProps {
  selections: CombinationsSelections;
  currentStep: FlowStep;
  showPills: boolean;
  onItemSelect: (
    itemKey: string,
    category: "activities" | "locations" | "days"
  ) => void;
  activityConfig: { options: FilterOption[] };
  locationConfig: { options: FilterOption[] };
  dayConfig: { options: FilterOption[] };
}

/**
 * Component for rendering the combinations filter section
 * Shows only the current step in the flow: day → activity → location
 */
const CombinationsFilterSection: React.FC<CombinationsFilterSectionProps> = ({
  selections,
  currentStep,
  showPills,
  onItemSelect,
  activityConfig,
  locationConfig,
  dayConfig
}) => {
  const { t } = useTranslation();

  const renderFilterSection = (
    options: FilterOption[],
    selectedKeys: string[],
    category: "activities" | "locations" | "days",
    translationKey: string,
    showSection: boolean
  ) => {
    if (!showSection) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          justifyContent: "flex-start",
          mb: 3
        }}
      >
        {options.map((option) => (
          <ActivityCard
            key={option.key}
            title={t(`${translationKey}.${option.key}`)}
            icon={
              option.iconSrc ? (
                <img
                  src={option.iconSrc}
                  alt={option.key}
                  style={{ width: 18, height: 18 }}
                />
              ) : undefined
            }
            selected={selectedKeys.includes(option.key)}
            onClick={() => onItemSelect(option.key, category)}
          />
        ))}
      </Box>
    );
  };

  // Show filters only if showPills is true and we're on the corresponding step
  // After the delay, pills are hidden and only messages are shown
  if (!showPills) {
    return null;
  }

  // Show only the current step's pills
  switch (currentStep) {
    case "day":
      return renderFilterSection(
        dayConfig.options,
        selections.days,
        "days",
        "filter_day",
        true
      );
    case "activity":
      return renderFilterSection(
        activityConfig.options,
        selections.activities,
        "activities",
        "filter_section",
        true
      );
    case "location":
      return renderFilterSection(
        locationConfig.options,
        selections.locations,
        "locations",
        "filter_location",
        true
      );
    default:
      return null;
  }
};

export default CombinationsFilterSection;
